import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function FoundItemList() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { hotelId } = useParams();

  useEffect(() => {
    if (!hotelId) return;

    const q = query(
      collection(db, "found-items"), 
      where("hotelId", "==", hotelId), 
      orderBy("createdAt", "desc")
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const itemsArray = [];
      querySnapshot.forEach((doc) => {
        itemsArray.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setItems(itemsArray);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [hotelId]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t('foundItemList.table.noData');
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  const handleNavigate = (path) => {
    navigate(`/${hotelId}${path}`);
  }

  return (
    <div className="item-list-container">
      <div className="item-list-header">
        <h2>{t('foundItemList.title')}</h2>
        <button onClick={() => handleNavigate('/found-items/register')} className="add-button">
          {t('foundItemList.buttons.registerNew')}
        </button>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <p>{t('foundItemList.loading')}</p>
        </div>
      ) : (
        <>
          {items.length === 0 ? (
            <div className="no-items">
              <p>{t('foundItemList.noItems')}</p>
            </div>
          ) : (
            <div className="items-table-container">
              <table className="items-table">
                <thead>
                  <tr>
                    <th>{t('foundItemList.table.headers.item')}</th>
                    <th>{t('foundItemList.table.headers.foundDate')}</th>
                    <th>{t('foundItemList.table.headers.location')}</th>
                    <th>{t('foundItemList.table.headers.owner')}</th>
                    <th>{t('foundItemList.table.headers.contact')}</th>
                    <th>{t('foundItemList.table.headers.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id} className="item-row">
                      <td className="item-cell">
                        <div className="item-type">{item.itemType || t('foundItemList.table.unknown')}</div>
                        <div className="item-name">{item.itemName || t('foundItemList.table.unnamedItem')}</div>
                      </td>
                      <td>{formatDate(item.dateFound)}</td>
                      <td>
                        <div>{item.locationLost || t('foundItemList.table.unknown')}</div>
                        <div className="storage-location">
                          <small>{t('foundItemList.table.storedAt')}: {item.storageLocation || t('foundItemList.table.noData')}</small>
                        </div>
                      </td>
                      <td>
                        {item.firstName || item.surname ? (
                          <div>{item.firstName} {item.surname}</div>
                        ) : (
                          <div className="no-data">{t('foundItemList.table.unknown')}</div>
                        )}
                      </td>
                      <td>
                        {item.phone ? (
                          <div className="contact-info">
                            <div>{item.phone}</div>
                            {item.email && <div className="email">{item.email}</div>}
                          </div>
                        ) : (
                          <div className="no-data">{t('foundItemList.table.noContactInfo')}</div>
                        )}
                      </td>
                      <td>
                        <button 
                          className="view-details-btn"
                          onClick={() => handleNavigate(`/found-items/${item.id}`)}
                        >
                          {t('foundItemList.buttons.view')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
} 